import { PromptsConfig, Prompt, AreaConfig } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let accessTokenProvider: (() => Promise<string>) | null = null;

export function setAccessTokenProvider(provider: () => Promise<string>) {
  accessTokenProvider = provider;
}

async function getAccessToken(): Promise<string> {
  if (!accessTokenProvider) {
    throw new Error('Access token provider not configured');
  }
  return await accessTokenProvider();
}

export interface AddPromptRequest {
  title: string;
  text: string;
  category: string;
}

export interface UpdatePromptRequest {
  title: string;
  text: string;
  category: string;
}

export interface AddAreaRequest {
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface UpdateAreaRequest {
  name: string;
  description: string;
  icon: string;
  color: string;
}

/**
 * Get custom prompts only
 */
export async function getCustomPrompts(): Promise<PromptsConfig> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get custom prompts: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Get merged prompts (standard + custom)
 */
export async function getMergedPrompts(): Promise<PromptsConfig> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/merged`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get merged prompts: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Add a new prompt to an existing area
 */
export async function addPrompt(areaId: string, prompt: AddPromptRequest): Promise<Prompt> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/areas/${encodeURIComponent(areaId)}/prompts`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(prompt),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to add prompt: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update an existing custom prompt
 */
export async function updatePrompt(promptId: string, prompt: UpdatePromptRequest): Promise<Prompt> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/prompts/${encodeURIComponent(promptId)}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(prompt),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to update prompt: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete a custom prompt
 */
export async function deletePrompt(promptId: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/prompts/${encodeURIComponent(promptId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to delete prompt: ${response.statusText}`);
  }
}

/**
 * Create a new custom area
 */
export async function addArea(area: AddAreaRequest): Promise<AreaConfig> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/areas`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(area),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to add area: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update an existing custom area
 */
export async function updateArea(areaId: string, area: UpdateAreaRequest): Promise<AreaConfig> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/areas/${encodeURIComponent(areaId)}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(area),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to update area: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete a custom area and all its prompts
 */
export async function deleteArea(areaId: string): Promise<void> {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE_URL}/customPrompts/areas/${encodeURIComponent(areaId)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || `Failed to delete area: ${response.statusText}`);
  }
}
